import { useCallback, useContext, useEffect, useRef, useState, type FC } from "react";
import { UNSAFE_NavigationContext, type Navigator } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "antd";
import { TriangleAlert } from "lucide-react";
import "./ExitCampaignGuard.scss";

type AskToExit = (_proceed: () => void) => void;

let askToExit: AskToExit | null = null;

let allowExit: (() => void) | null = null;

export const runAfterExitConfirm = (proceed: () => void) => {
  if (askToExit) {
    askToExit(proceed);
    return;
  }
  proceed();
};

export const exitWithoutConfirm = (proceed: () => void) => {
  if (allowExit) {
    allowExit();
  }
  proceed();
};

const getHistoryIndex = (): number | null =>
  (window.history.state as { idx?: number } | null)?.idx ?? null;

/** Holds back navigation while `active` and exposes the pending exit for the caller to confirm. */
export const useExitCampaignGuard = (active: boolean) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [isPrompting, setIsPrompting] = useState(false);
  const proceedRef = useRef<(() => void) | null>(null);
  const allowRef = useRef(false);

  useEffect(() => {
    if (!active) {
      return;
    }

    allowRef.current = false;
    const ask: AskToExit = proceed => {
      proceedRef.current = proceed;
      setIsPrompting(true);
    };
    askToExit = ask;
    allowExit = () => {
      allowRef.current = true;
    };

    const original = { push: navigator.push, replace: navigator.replace, go: navigator.go };
    navigator.push = (...args: Parameters<Navigator["push"]>) =>
      allowRef.current ? original.push(...args) : ask(() => original.push(...args));
    navigator.replace = (...args: Parameters<Navigator["replace"]>) =>
      allowRef.current ? original.replace(...args) : ask(() => original.replace(...args));
    navigator.go = (delta: number) =>
      allowRef.current ? original.go(delta) : ask(() => original.go(delta));

    // The router handles browser back/forward itself, so it is stopped in the capture phase,
    // the history is put back where it was, and the move only happens once the user confirms.
    let isReverting = false;
    const currentIndex = getHistoryIndex();
    const handlePopState = (event: PopStateEvent) => {
      if (allowRef.current) {
        return;
      }
      event.stopImmediatePropagation();
      if (isReverting) {
        isReverting = false;
        return;
      }
      const nextIndex = getHistoryIndex();
      const delta = currentIndex !== null && nextIndex !== null ? nextIndex - currentIndex : -1;
      isReverting = true;
      window.history.go(-delta);
      ask(() => window.history.go(delta));
    };
    window.addEventListener("popstate", handlePopState, true);

    return () => {
      window.removeEventListener("popstate", handlePopState, true);
      navigator.push = original.push;
      navigator.replace = original.replace;
      navigator.go = original.go;
      if (askToExit === ask) {
        askToExit = null;
        allowExit = null;
      }
    };
  }, [active, navigator]);

  const cancelExit = useCallback(() => {
    proceedRef.current = null;
    setIsPrompting(false);
  }, []);

  const confirmExit = useCallback(() => {
    allowRef.current = true;
    setIsPrompting(false);
    proceedRef.current?.();
    proceedRef.current = null;
  }, []);

  return { isPrompting, confirmExit, cancelExit };
};

interface ExitCampaignGuardProps {
  active: boolean;
  /** When set, the prompt offers to save the campaign as a draft instead of a plain exit. */
  onSaveDraft?: () => Promise<boolean>;
}

const ExitCampaignGuard: FC<ExitCampaignGuardProps> = ({ active, onSaveDraft }) => {
  const { t } = useTranslation();
  const { isPrompting, confirmExit, cancelExit } = useExitCampaignGuard(active);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveAndExit = async () => {
    if (!onSaveDraft) {
      return;
    }
    setIsSaving(true);
    try {
      if (await onSaveDraft()) {
        confirmExit();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      className="confirmation-modal-content exit-campaign-modal"
      centered
      open={isPrompting}
      footer={null}
      onCancel={cancelExit}
    >
      <div className="confirmation-modal-wrapper">
        <div className="confirmation-modal-header">
          <TriangleAlert className="exit-campaign-modal__icon" size={20} aria-hidden />
          <label className="exit-campaign-modal__title">
            {t(onSaveDraft ? "campaignFlow.common.saveProgressTitle" : "campaignFlow.common.exitTitle")}
          </label>
        </div>
        <div className="confirmation-modal-body exit-campaign-modal__description">
          {t(
            onSaveDraft
              ? "campaignFlow.common.saveProgressDescription"
              : "campaignFlow.common.exitDescription"
          )}
        </div>
        {onSaveDraft ? (
          <div className="confirmation-modal-footer exit-campaign-modal__footer">
            <Button
              type="link"
              className="exit-campaign-modal__discard"
              onClick={confirmExit}
              disabled={isSaving}
            >
              {t("campaignFlow.common.discardExit")}
            </Button>
            <Button className="button button--outline" onClick={cancelExit} disabled={isSaving}>
              {t("campaignFlow.common.keepEditing")}
            </Button>
            <Button className="button button--primary" onClick={handleSaveAndExit} loading={isSaving}>
              {t("campaignFlow.common.saveExit")}
            </Button>
          </div>
        ) : (
          <div className="confirmation-modal-footer">
            <Button className="button button--outline" onClick={confirmExit}>
              {t("campaignFlow.common.exit")}
            </Button>
            <Button className="button button--primary" onClick={cancelExit}>
              {t("campaignFlow.common.cancel")}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExitCampaignGuard;
